import React from 'react';
import { Droplets, LogIn, UserPlus, ChevronRight, Zap } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useUtilityInfo } from '../../../auth/hooks';
import { useGuestPortalBranding } from '../../hooks';
import { useTranslation } from 'react-i18next';

export const ManageAccountLanding = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const tenant = localStorage.getItem('tenant');
  const utilityName = localStorage.getItem('utilityName');
  
  const { data: utilityInfo } = useUtilityInfo(
    { tenant_alias: tenant || '', utility_alias: utilityName || '' },
    { enabled: !!tenant && !!utilityName }
  );

  const utilityId = utilityInfo?.result?.id;

  const { data: brandingDataRes } = useGuestPortalBranding(
    { remote_utility_id: String(utilityId) },
    { enabled: !!utilityId }
  );

  const brandingData = utilityInfo?.result?.branding || {};
  const portalBranding = brandingData?.portalBranding || {};
  const logoUrl = portalBranding?.logo || '';
  const brandText = portalBranding?.brandText || '';
  const displayUtilityName = utilityInfo?.result?.name || 'SMART360';
  
  const guestPortalBranding = brandingDataRes?.result || brandingDataRes;
  const headline = guestPortalBranding?.leftPanelText?.headline || t("Manage your utility account");
  const supportingText = guestPortalBranding?.leftPanelText?.supportingText || t("Sign in or create an account to access bills, consumption history, and more.");

  const bgConfig = guestPortalBranding?.leftPanelBackground;
  let defaultBgClass = "bg-gradient-to-br from-[#0a1526] via-[#103063] to-[#1e52a8]";
  const bgStyle: React.CSSProperties = {};

  if (bgConfig) {
    if (bgConfig.type === 'gradient' && bgConfig.startColour && bgConfig.endColour) {
      defaultBgClass = "";
      bgStyle.backgroundImage = `linear-gradient(${bgConfig.gradientDirection || 'to bottom right'}, #${bgConfig.startColour}, #${bgConfig.endColour})`;
    } else if ((bgConfig.type === 'image' || bgConfig.type === 'photo') && (bgConfig.backgroundImage || bgConfig.imageUrl)) {
      defaultBgClass = "";
      bgStyle.backgroundImage = `url(${bgConfig.backgroundImage || bgConfig.imageUrl})`;
      bgStyle.backgroundSize = 'contain';
      bgStyle.backgroundPosition = 'center';
      bgStyle.backgroundRepeat = 'no-repeat';
    } else if (bgConfig.type === 'solid' && bgConfig.colour) {
      defaultBgClass = "";
      bgStyle.backgroundColor = `#${bgConfig.colour}`;
    }
  }

  const loginPath = tenant && utilityName ? `/login/${tenant}/${utilityName}` : '/login';
  const createAccountPath = tenant && utilityName ? `/create-account/${tenant}/${utilityName}` : '/create-account';

  return (
    <div className={`w-full h-full text-white p-8 lg:p-12 flex flex-col justify-center relative ${defaultBgClass}`} style={bgStyle}>

      {/* Background dot pattern */}
      {(!bgConfig || (bgConfig.type !== 'image' && bgConfig.type !== 'photo')) && (
        <div 
          className="absolute inset-0 opacity-10 pointer-events-none" 
          style={{ backgroundImage: 'radial-gradient(circle, white 1px, transparent 1px)', backgroundSize: '30px 30px' }}
        ></div>
      )}

      <div className="max-w-md mx-auto w-full relative z-10 flex flex-col">
        <div className="flex items-center gap-3 mb-10">
          {logoUrl ? (
            <img src={logoUrl} alt={displayUtilityName} className="h-10 object-contain rounded-lg bg-white/10 p-1 border border-white/20" />
          ) : brandText ? (
            <div className="font-bold tracking-widest text-2xl leading-tight text-left text-white">
              {brandText}
            </div>
          ) : (
            <div className="flex items-center gap-3">
              <div className="p-2 bg-white/10 rounded-xl border border-white/20">
                <Zap className="text-blue-300 h-5 w-5" />
              </div>
              <div className="font-bold tracking-widest text-base leading-tight text-left ">
                {displayUtilityName}<br/><span className="text-blue-300 text-[10px]">{t("UTILITY PORTAL")}</span>
              </div>
            </div>
          )}
        </div>

        <h1 className="text-3xl font-bold mb-2 text-left">{headline}</h1>
        <p className="text-blue-200 text-sm lg:text-base mb-8 leading-relaxed text-left">
          {supportingText}
        </p>

        <div className="space-y-3 w-full max-w-[340px]">
          <button 
            onClick={() => navigate(loginPath)}
            className="w-full flex items-center justify-between p-4 rounded-xl border border-white/10 bg-white/5 hover:bg-white/10 transition-colors group"
          >
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-blue-500/20 flex items-center justify-center text-blue-300 shrink-0">
                <LogIn className="w-5 h-5" />
              </div>
              <div className="text-left">
                <h3 className="font-bold text-base">{t("Sign In")}</h3>
                <p className="text-blue-200/70 text-xs">{t("Access your account")}</p>
              </div>
            </div>
            <ChevronRight className="w-4 h-4 text-white/40 group-hover:text-white transition-colors shrink-0" />
          </button>

          <button 
            onClick={() => navigate(createAccountPath)}
            className="w-full flex items-center justify-between p-4 rounded-xl border border-white/10 bg-white/5 hover:bg-white/10 transition-colors group"
          >
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-teal-500/20 flex items-center justify-center text-teal-400 shrink-0">
                <UserPlus className="w-5 h-5" />
              </div>
              <div className="text-left">
                <h3 className="font-bold text-base">{t("Create Account")}</h3>
                <p className="text-blue-200/70 text-xs">{t("Register with your consumer number")}</p>
              </div>
            </div>
            <ChevronRight className="w-4 h-4 text-white/40 group-hover:text-white transition-colors shrink-0" />
          </button>
        </div>
      </div>

    </div>
  );
};